<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { answer, dialogState } from '../dialogs';
import { t } from '../i18n';

/** Renders the in-app confirm / prompt / alert requested through dialogs.ts. */
const input = ref<HTMLInputElement | null>(null);
const okBtn = ref<HTMLButtonElement | null>(null);
const text = ref('');
let returnFocus: HTMLElement | null = null;

watch(
  () => dialogState.current,
  async (d, prev) => {
    if (d && !prev) returnFocus = document.activeElement as HTMLElement | null;
    if (!d) {
      returnFocus?.focus?.();
      return;
    }
    text.value = d.value ?? '';
    await nextTick();
    if (d.kind === 'prompt') {
      input.value?.focus();
      input.value?.select();
    } else okBtn.value?.focus();
  },
);

function ok(): void {
  const d = dialogState.current;
  if (!d) return;
  answer(d.kind === 'prompt' ? text.value : true);
}

function cancel(): void {
  const d = dialogState.current;
  if (!d) return;
  answer(d.kind === 'prompt' ? null : d.kind === 'alert' ? true : false);
}
</script>

<template>
  <Transition name="pop">
    <div v-if="dialogState.current" class="dialog-backdrop app-dialog" @pointerdown.self="cancel" @keydown.esc.stop="cancel">
      <form class="box" role="alertdialog" aria-modal="true" :aria-label="dialogState.current.title ?? dialogState.current.message" @submit.prevent="ok">
        <h3 v-if="dialogState.current.title">{{ dialogState.current.title }}</h3>
        <p class="msg">{{ dialogState.current.message }}</p>
        <input v-if="dialogState.current.kind === 'prompt'" ref="input" v-model="text" class="field" maxlength="200" :aria-label="dialogState.current.message" />
        <div class="actions">
          <button v-if="dialogState.current.kind !== 'alert'" type="button" class="pill" @click="cancel">{{ dialogState.current.cancelLabel ?? t('common.cancel') }}</button>
          <button ref="okBtn" type="submit" class="pill primary" :class="{ danger: dialogState.current.danger }">{{ dialogState.current.okLabel ?? t('common.ok') }}</button>
        </div>
      </form>
    </div>
  </Transition>
</template>

<style scoped>
.app-dialog {
  z-index: 4000;
  background: var(--backdrop);
}
.box {
  width: min(400px, calc(100vw - 32px));
  padding: 20px 20px 16px;
  background: var(--panel);
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.22);
}
h3 {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
}
.msg {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-2);
  white-space: pre-line;
}
.field {
  width: 100%;
  height: 36px;
  margin-top: 12px;
  padding: 0 14px;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  outline: none;
}
.field:focus {
  border-color: var(--border-strong);
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}
.pill {
  height: 34px;
  padding: 0 16px;
  font-size: 13px;
}
.pill.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.pill.primary:hover {
  background: var(--accent-2);
  color: var(--on-accent);
}
.pill.primary.danger {
  background: var(--danger);
  border-color: var(--danger);
}
.pop-enter-active,
.pop-leave-active {
  transition: opacity 0.15s ease;
}
.pop-enter-from,
.pop-leave-to {
  opacity: 0;
}
</style>
